import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { environment } from '@env/environment';
import { UiStateService } from '@app/core/services';
import { ConfigService } from '@app/core/services/config.service';
import { MatDialog } from '@angular/material/dialog';
import { delay, filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { switchTap } from '@app/core/functions/switch-tap';
import { ConfirmDialogComponent, DialogResponse } from '@app/shared/components/confirm-dialog/confirm-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class NoraRecorderService {
  private activeRecorders: Map<string, any> = new Map();

  private apiBaseUrl = environment.noraV2.noraApiBaseUrl;

  isRecording$ = new BehaviorSubject<boolean>(false);
  recordingPatientId$ = new BehaviorSubject<string>('');
  isMountingRecorder = new BehaviorSubject<boolean>(false);

  constructor(
    private uiStateService: UiStateService,
    private configService: ConfigService
  ) {}

  registerRecorder(consultationId: string, recorder: any): void {
    this.activeRecorders.set(consultationId, recorder);
  }

  getRecorder(consultationId: string): any {
    return this.activeRecorders.get(consultationId);
  }

  unregisterRecorder(consultationId: string): void {
    const recorder = this.activeRecorders.get(consultationId);
    if (recorder) {
      try {
        recorder.cleanup();
      } catch (err) {
        console.error('Error cleaning up recorder:', err);
      }
      this.activeRecorders.delete(consultationId);
    }
  }

  isPatientRecordingInProgress(patientId: string): boolean {
    return this.isRecording$.value && patientId === this.recordingPatientId$.value;
  }

  setIsRecording(isRecording: boolean) {
    this.isRecording$.next(isRecording);
  }

  setRecordingPatientId(patientId: string) {
    this.recordingPatientId$.next(patientId);
  }

  getConfiguration(doctorId: string, consultationId: string) {
    return this.configService.clinicalConfig$.pipe(
      take(1),
      map(config => ({
        apiKey: config.norav2apiKey,
        doctorID: doctorId,
        consultationID: consultationId,
        apiBaseUrl: this.apiBaseUrl,
      }))
    );
  }

  cleanupAll(): void {
    this.activeRecorders.forEach((recorder, consultationId) => {
      try {
        recorder.cleanup();
      } catch (err) {
        console.error(`Error cleaning up recorder for ${consultationId}:`, err);
      }
    });
    this.activeRecorders.clear();
  }

  setIsMountingRecorder(isMounting: boolean) {
    this.isMountingRecorder.next(isMounting);
  }

  toggleRecorder$(dialog: MatDialog, currentPatientId$: Observable<string>) {
    return this.uiStateService.noraRecorderVisible$.pipe(
      take(1),
      withLatestFrom(currentPatientId$, this.isRecording$, this.recordingPatientId$),
      switchTap(([visible, patientId, isRecording, recordingPatientId]) => {
        if (visible && recordingPatientId !== patientId) {
          if (isRecording) {
            return dialog
              .open(ConfirmDialogComponent, {
                width: '450px',
                data: {
                  title: 'Recording in Progress',
                  message: 'A recording is already in progress for another patient. Do you want to discard it and start a new one?',
                },
              })
              .afterClosed()
              .pipe(
                filter(response => !!response && response.key === DialogResponse.YES),
                switchMap(() => this.toggleNoraNewPatient$())
              );
          } else {
            return this.toggleNoraNewPatient$();
          }
        } else {
          return of(null).pipe(tap(() => this.uiStateService.toggleNoraRecorder()));
        }
      })
    );
  }

  private toggleNoraNewPatient$() {
    return of(null).pipe(
      tap(() => this.uiStateService.toggleNoraRecorder()), // first destroy other patient's recorder
      delay(0),
      tap(() => this.uiStateService.toggleNoraRecorder()) // Then create new recorder for current patient
    );
  }
}
